import { useRef, useCallback, useEffect } from "react";

// Continuous, teleprompter-style reading scroller.
//
// A SINGLE requestAnimationFrame loop eases the container's scrollTop toward one
// continuously-updated target. The target keeps the ACTIVE line at a fixed anchor
// (~42% of the viewport) AND interpolates sub-line using the active word's
// horizontal progress across the reading column — so the document glides upward
// smoothly and continuously (small, frequent deltas) instead of stepping once
// every couple of lines. There is exactly one loop and one target, so scroll
// animations never stack; pausing stops instantly and resuming continues without
// a catch-up jump. Stable from 150 to 800+ WPM. Respects prefers-reduced-motion.

const ANCHOR = 0.42; // active line target position (fraction of viewport height)

export function useSmoothFollow(containerRef, { reducedMotion = false } = {}) {
    const rafRef = useRef(null);
    const targetRef = useRef(null);

    const stop = useCallback(() => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
        targetRef.current = null;
    }, []);

    const tick = useCallback(() => {
        const c = containerRef.current;
        if (!c || targetRef.current == null) { rafRef.current = null; return; }
        const cur = c.scrollTop;
        const diff = targetRef.current - cur;
        if (Math.abs(diff) < 0.4) {
            c.scrollTop = targetRef.current;
            targetRef.current = null;
            rafRef.current = null;
            return;
        }
        // Ease a modest fraction each frame → smooth glide, frequent tiny deltas.
        c.scrollTop = cur + diff * 0.14;
        rafRef.current = requestAnimationFrame(tick);
    }, [containerRef]);

    const follow = useCallback((el, opts = {}) => {
        const c = containerRef.current;
        if (!c || !el) return;
        const immediate = opts.immediate === true;
        const cRect = c.getBoundingClientRect();
        const eRect = el.getBoundingClientRect();

        // Vertical position of the active line's top within the scrollable content.
        const lineTop = c.scrollTop + (eRect.top - cRect.top);
        const lineH = eRect.height || parseFloat(getComputedStyle(el).lineHeight) || 30;

        // Sub-line interpolation: how far the active word sits across the column.
        const surf = el.closest(".rg-surface");
        let fracX = 0;
        if (surf) {
            const sRect = surf.getBoundingClientRect();
            fracX = (eRect.left + eRect.width / 2 - sRect.left) / Math.max(1, sRect.width);
            fracX = Math.max(0, Math.min(1, fracX));
        }

        const desired = lineTop + fracX * lineH - c.clientHeight * ANCHOR;
        const max = Math.max(0, c.scrollHeight - c.clientHeight);
        const target = Math.max(0, Math.min(max, desired));

        if (immediate || reducedMotion) {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
            targetRef.current = null;
            c.scrollTop = target;
            return;
        }
        targetRef.current = target;
        if (!rafRef.current) rafRef.current = requestAnimationFrame(tick);
    }, [containerRef, tick, reducedMotion]);

    useEffect(() => () => stop(), [stop]);
    return { follow, stop };
}

export const prefersReducedMotion = () => {
    try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; }
    catch (e) { return false; }
};
